import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { FlexLayoutModule } from '@angular/flex-layout';

import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MomentModule } from 'ngx-moment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SignInComponent } from './auth/sign-in/sign-in.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ChangePasswordComponent } from './auth/change-password/change-password.component';
import { WelcomeComponent } from './welcome/welcome.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import { MatToolbarModule} from '@angular/material/toolbar';
import { MatButtonModule} from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import {MatStepperModule} from '@angular/material/stepper';
import {MatCardModule} from '@angular/material/card';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatTableModule} from '@angular/material/table';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatDialogModule} from '@angular/material/dialog';
import {MatRadioModule} from '@angular/material/radio';
import {MatListModule} from '@angular/material/list';
import {MatSelectModule} from '@angular/material/select';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatChipsModule} from '@angular/material/chips';
import {MatMenuModule} from '@angular/material/menu';
import {MatTreeModule} from '@angular/material/tree';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatPaginatorModule} from '@angular/material/paginator';

import { HeaderComponent } from './nav/header/header.component';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';

import {JwtTokenInterceptor} from './shared/interceptors/jwt-token.interceptor';
import { AssetDetailComponent, DialogDeleteAssetDialog } from './assets/asset-detail/asset-detail.component';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatNativeDateModule, MAT_DATE_FORMATS } from '@angular/material/core';
import { DebtListComponent } from './debts/debt-list/debt-list.component';
import { MainMenuComponent } from './nav/main-menu/main-menu.component';
import { DebtDetailComponent, DialogDeleteDebtDialog } from './debts/debt-detail/debt-detail.component';
import { AssetListComponent } from './assets/asset-list/asset-list.component';
import { ExploreNewPurchaseGuideComponent } from './guides/explore-new-purchase-guide/explore-new-purchase-guide.component';
import { FabMenuComponent } from './nav/fab-menu/fab-menu.component';

import {CurrencyMaskModule} from 'ng2-currency-mask';
import { BankAccountsListComponent } from './bank-accounts/bank-accounts-list/bank-accounts-list.component';
import { ProviderCredentialListComponent } from './bank-accounts/provider-credential-list/provider-credential-list.component';
import { ProviderCredentialAddUpdateComponent } from './bank-accounts/provider-credential-add-update/provider-credential-add-update.component';
import { BankAccountDetailComponent } from './bank-accounts/bank-account-detail/bank-account-detail.component';
import { NgChartsModule } from 'ng2-charts';
import { TxnCatsPieChartComponent } from './dashboard/widgets/txn-cats-pie-chart/txn-cats-pie-chart.component';
import { TxnCatGroupsOverTimeBarChartComponent } from './dashboard/widgets/txn-cat-groups-over-time-bar-chart/txn-cat-groups-over-time-bar-chart.component';
import { EnrichmentRulesetListComponent, DialogDeleteRulesetDialog } from './bank-accounts/enrichment-ruleset-list/enrichment-ruleset-list.component';
import { EnrichmentRuleAddUpdateComponent } from './bank-accounts/enrichment-rule-add-update/enrichment-rule-add-update.component';
import { EnrichmentRulesetListRulesComponent } from './bank-accounts/enrichment-ruleset-list-rules/enrichment-ruleset-list-rules.component';
import { AdminOverviewComponent } from './admin/admin-overview/admin-overview.component';
import { DailyBalanceLineChartComponent } from './shared/charts/daily-balance-line-chart/daily-balance-line-chart.component';
import { IncomeAndExpensesLineChartComponent } from './dashboard/widgets/income-and-expenses-line-chart/income-and-expenses-line-chart.component';
import { AccountsListComponent } from './dashboard/widgets/accounts-list/accounts-list.component';
import { DailyBalanceAllAccountsLineChartComponent } from './dashboard/widgets/daily-balance-all-accounts-line-chart/daily-balance-all-accounts-line-chart.component';
import { BalanceAllAccountsStatComponent } from './dashboard/widgets/balance-all-accounts-stat/balance-all-accounts-stat.component';
import { IncomeStatComponent } from './dashboard/widgets/income-stat/income-stat.component';
import { ExpensesStatComponent } from './dashboard/widgets/expenses-stat/expenses-stat.component';
import { TxnCatGroupsStatsTableComponent } from './dashboard/widgets/txn-cat-groups-stats-table/txn-cat-groups-stats-table.component';
import { TxnCategoriesStatsTableComponent } from './dashboard/widgets/txn-categories-stats-table/txn-categories-stats-table.component';
import { SpendByDayBarChartComponent } from './dashboard/widgets/spend-by-day-bar-chart/spend-by-day-bar-chart.component';

@NgModule({
  declarations: [
    AppComponent,
    SignInComponent,
    DashboardComponent,
    ChangePasswordComponent,
    WelcomeComponent,
    HeaderComponent,
    ForgotPasswordComponent,
    AssetDetailComponent,
    DialogDeleteAssetDialog,
    DebtListComponent,
    MainMenuComponent,
    DebtDetailComponent,
    DialogDeleteDebtDialog,
    AssetListComponent,
    ExploreNewPurchaseGuideComponent,
    FabMenuComponent,
    BankAccountsListComponent,
    ProviderCredentialListComponent,
    ProviderCredentialAddUpdateComponent,
    BankAccountDetailComponent,
    TxnCatsPieChartComponent,
    TxnCatGroupsOverTimeBarChartComponent,
    EnrichmentRulesetListComponent,
    EnrichmentRuleAddUpdateComponent,
    EnrichmentRulesetListRulesComponent,
    DialogDeleteRulesetDialog,
    AdminOverviewComponent,
    DailyBalanceLineChartComponent,
    IncomeAndExpensesLineChartComponent,
    AccountsListComponent,
    DailyBalanceAllAccountsLineChartComponent,
    BalanceAllAccountsStatComponent,
    IncomeStatComponent,
    ExpensesStatComponent,
    TxnCatGroupsStatsTableComponent,
    TxnCategoriesStatsTableComponent,
    SpendByDayBarChartComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    ReactiveFormsModule,
    BrowserAnimationsModule, 
    MatInputModule,
    MatIconModule,
    MatToolbarModule,
    MatButtonModule,
    MatFormFieldModule,
    MatStepperModule,
    MatCardModule,
    MatDatepickerModule,
    MatTableModule,
    MatSidenavModule,
    MatSnackBarModule,
    MatCheckboxModule,
    MatDialogModule,
    MatListModule,
    FlexLayoutModule,
    MatSelectModule,
    MatRadioModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatChipsModule,
    MatMenuModule,
    MatTreeModule,
    MatTooltipModule,
    MatPaginatorModule,
    MomentModule.forRoot(),
    CurrencyMaskModule,
    NgChartsModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtTokenInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
